import styles from "./TextText.module.scss";
import TextTextBlockProps from "./TextTextProps.interface";

import React, { FunctionComponent } from "react";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getTextAlignment from "helpers/getTextAlignment";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import Paragraph from "components/Paragraph/Paragraph";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useAnchor from "blocks/hooks/useAnchor";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";

const TextText: FunctionComponent<TextTextBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    if (props.settings?.headlineAlignment) {
        wrapperStyles["--headline-alignment"] = getTextAlignment(props.settings?.headlineAlignment);
    }

    if (props.settings?.textAlignment) {
        wrapperStyles["--text-alignment"] = getTextAlignment(props.settings?.textAlignment);
    }

    if (props.settings?.textWidth) {
        wrapperClasses.push(styles[`width_${props.settings.textWidth}`]);
    }

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} style={{...wrapperStyles, ...splitStyles}} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, inViewClasses)} ref={inViewElementRef}>
                {/* Tagline */}
                {props.content.tagline ? (
                    <span className={styles.tagline}>{props.content.tagline}</span>
                ) : null}

                {/* Headline */}
                {props.content.headline ? (
                    <DynamicHeading className={combineClasses(styles.headline, headlineCaseClass)} text={props.content.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight} />
                ) : null}

                <div className={styles.content}>
                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}

                    {/* Text 2 */}
                    {props.content.bodyText2 ? (
                        <Paragraph className={styles.text} innerHTML={props.content.bodyText2} />
                    ) : null}
                </div>

                {/* Buttons */}
                {props.content.buttons && props.content.buttons.length > 0 ? (
                    <div className={styles.buttons}>
                        {props.content.buttons.map((button, index) => {
                            return (
                                <Button key={index} to={button.content.link} className={styles.button} variant={button.content.variant} {...button.content}>{button.content.label}</Button>
                            );
                        })}
                    </div>
                ) : null}
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default TextText;